import { Link } from "react-router-dom";
import { Seo } from "../components/site/Seo";
import { HomeHero } from "../components/site/HomeHero";
import { Marquee } from "../components/site/Marquee";
import { Reveal, SectionHead, Eyebrow, MaskLines } from "../components/site/Motion";
import { CTAButton } from "../components/site/CTAButton";
import { SolutionsList } from "../components/site/SolutionsList";
import { ProcessSteps } from "../components/site/ProcessSteps";
import { ArticleCard } from "../components/site/ArticleCard";
import { CTABand } from "../components/site/CTABand";
import { LineIcon } from "../components/site/LineIcon";
import { IMG, STAKEHOLDERS, PILLARS, WHAT_WE_DO } from "../content/site";
import { ArrowUpRight } from "lucide-react";
import { ARTICLES } from "../content/insights";

const Intro = () => (
  <section className="bg-[#F9F8F3] py-24 md:py-36" data-testid="home-intro">
    <div className="mx-auto grid max-w-[1440px] gap-14 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Reveal><Eyebrow>Who we are</Eyebrow></Reveal>
        <p className="font-display mt-8 text-3xl sm:text-4xl lg:text-[3.2rem] font-medium leading-[1.12] tracking-tight text-[#082619]">
          <MaskLines inView lines={[
            "Solar procurement is a",
            <><span className="font-serif-i text-[#3f8a0f]">commercial</span> decision as much</>,
            "as a technical one.",
          ]} />
        </p>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#4C6053]">
            Solitaire India is not an installer. We are a Mumbai-based renewable energy and solar advisory partner, helping commercial and industrial consumers choose the right solar model, evaluate proposals objectively and connect with the right developers and EPC partners.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <CTAButton to="/about" variant="forest" testId="home-intro-about">About Solitaire India</CTAButton>
          </div>
        </Reveal>
      </div>
      <Reveal className="lg:col-span-5" delay={0.1}>
        <div className="relative aspect-[4/5] overflow-hidden rounded-t-full">
          <img src={IMG.engineering} alt="Engineers reviewing an industrial energy project" loading="lazy" className="h-full w-full object-cover" />
          <span className="eyebrow absolute bottom-5 left-5 rounded-full bg-[#082619] px-4 py-2 !text-[0.6rem] text-[#F9F8F3]">Techno-commercial clarity</span>
        </div>
      </Reveal>
    </div>
  </section>
);

const WhatWeDo = () => (
  <section className="border-t border-[#E5E1D3] bg-[#F9F8F3] pb-24 md:pb-32" data-testid="home-what-we-do">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <div className="grid border-b border-[#E5E1D3] md:grid-cols-3 md:divide-x md:divide-[#E5E1D3]">
        {WHAT_WE_DO.map((w, i) => (
          <Reveal key={w.id} delay={i * 0.1} className="h-full">
            <Link to={w.to} className={`group flex h-full flex-col border-t border-[#E5E1D3] py-12 md:border-t-0 ${i === 0 ? "md:pr-10" : "md:px-10"}`} data-testid={`what-we-do-${w.id}`}>
              <div className="flex items-center justify-between">
                <span className="grid h-14 w-14 place-items-center rounded-full bg-[#082619] text-[#74DC26] transition-transform duration-500 group-hover:scale-110"><LineIcon name={w.icon} /></span>
                <span className="font-mono text-xs text-[#4C6053]">0{i + 1}</span>
              </div>
              <h3 className="font-display mt-10 text-2xl md:text-3xl font-medium text-[#082619]">{w.title}</h3>
              <p className="mt-4 flex-1 text-sm md:text-base leading-relaxed text-[#4C6053]">{w.text}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#082619]">Learn more <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Bento = () => (
  <section className="bg-[#F2EFE7] py-24 md:py-32" data-testid="home-stakeholders">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <SectionHead eyebrow="Who we connect" title={["One advisory desk.", "Every side of the table."]} intro="We bring consumers, developers, EPC companies and investors together around opportunities that have been properly understood." />
      <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2">
        <Reveal className="relative min-h-[320px] overflow-hidden rounded-2xl md:col-span-2 lg:row-span-2">
          <img src={IMG.farm} alt="Ground-mounted utility-scale solar farm" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#082619]/90 via-[#082619]/30 to-transparent" />
          <div className="absolute bottom-0 p-8 md:p-10">
            <p className="font-display text-2xl md:text-3xl font-medium leading-tight text-white">Demand, understood. Partners, aligned.</p>
            <p className="mt-3 max-w-md text-sm text-[#E6EDE8]">Our role is to make every conversation between consumer and partner a well-informed one.</p>
          </div>
        </Reveal>
        {STAKEHOLDERS.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08} className="h-full">
            <div className={`flex h-full flex-col justify-between rounded-2xl p-7 ${i === 0 ? "bg-[#082619] text-[#F9F8F3]" : i === 3 ? "bg-[#74DC26] text-[#082619]" : "bg-white text-[#082619]"}`} data-testid={`stakeholder-${s.id}`}>
              <LineIcon name={s.icon} className="h-7 w-7" />
              <div className="mt-10">
                <h3 className="font-display text-xl font-medium">{s.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-[#B4C7BA]" : "text-[#082619]/75"}`}>{s.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Solutions = () => (
  <section className="relative bg-[#082619] py-24 md:py-32 grain" data-testid="home-solutions">
    <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10">
      <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-end">
        <SectionHead tone="light" eyebrow="Solar solutions" title={["Six ways to bring", "renewables into your business."]} />
        <Reveal className="flex flex-wrap gap-3">
          <CTAButton to="/solutions#finder" variant="ghostLight" testId="home-solutions-finder">Try the Solution Finder</CTAButton>
          <CTAButton to="/solutions" testId="home-solutions-all">View all solutions</CTAButton>
        </Reveal>
      </div>
      <div className="mt-16"><SolutionsList /></div>
      <Reveal>
        <p className="mt-10 max-w-3xl text-xs leading-relaxed text-[#B4C7BA]">Availability, savings and eligibility depend on project economics, applicable regulations, location and consumer requirements.</p>
      </Reveal>
    </div>
  </section>
);

const Process = () => (
  <section className="bg-[#F9F8F3] py-24 md:py-32" data-testid="home-process">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <SectionHead eyebrow="How we work" title={["A clear path from", "requirement to implementation."]} />
      <div className="mt-16"><ProcessSteps /></div>
      <Reveal className="mt-12 flex flex-wrap gap-3">
        <CTAButton to="/services" variant="forest" testId="home-process-services">Our Services</CTAButton>
        <CTAButton to="/contact" variant="ghostDark" testId="home-process-enquire">Start an Assessment</CTAButton>
      </Reveal>
    </div>
  </section>
);

const Philosophy = () => (
  <section className="bg-[#F2EFE7] py-24 md:py-32" data-testid="home-philosophy">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <Reveal><Eyebrow>Our philosophy</Eyebrow></Reveal>
      <div className="mt-10 divide-y divide-[#082619]/15 border-y border-[#082619]/15">
        {PILLARS.map((p, i) => (
          <div key={p.word} className="grid items-baseline gap-4 py-8 md:grid-cols-12 md:py-10" data-testid={`pillar-${p.word.toLowerCase()}`}>
            <span className="font-mono text-sm text-[#3f8a0f] md:col-span-1">0{i + 1}</span>
            <h3 className="font-display text-5xl sm:text-6xl lg:text-8xl font-medium tracking-tighter text-[#082619] md:col-span-6">
              <MaskLines inView lines={[i === 1 ? <span className="font-serif-i text-[#3f8a0f]">{p.word}</span> : p.word]} />
            </h3>
            <Reveal className="md:col-span-5" delay={0.15}><p className="text-base leading-relaxed text-[#4C6053]">{p.text}</p></Reveal>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PartnerTeaser = () => (
  <section className="bg-[#F9F8F3] py-24 md:py-32" data-testid="home-partner">
    <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 md:px-10 lg:grid-cols-2">
      <Reveal className="relative aspect-[5/4] overflow-hidden rounded-2xl [clip-path:polygon(0_0,100%_0,100%_86%,86%_100%,0_100%)]">
        <img src={IMG.park} alt="Utility-scale solar park infrastructure" loading="lazy" className="h-full w-full object-cover" />
      </Reveal>
      <div>
        <SectionHead eyebrow="For developers, EPCs & investors" title={["Collaborate on", "qualified C&I opportunities."]} intro="We work with solar developers, EPC companies and renewable energy investors who want to engage with commercial and industrial demand that has been properly assessed." />
        <Reveal className="mt-10"><CTAButton to="/partner" variant="forest" testId="home-partner-cta">Partner With Us</CTAButton></Reveal>
      </div>
    </div>
  </section>
);

const Insights = () => (
  <section className="border-t border-[#E5E1D3] bg-[#F9F8F3] py-24 md:py-32" data-testid="home-insights">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <SectionHead eyebrow="Insights" title={["Solar procurement,", "explained simply."]} />
        <Reveal><Link to="/insights" className="link-underline text-sm font-semibold text-[#082619]" data-testid="home-insights-all">All insights & FAQs</Link></Reveal>
      </div>
      <div className="mt-14 grid gap-10 md:grid-cols-3">
        {ARTICLES.slice(0, 3).map((a, i) => <ArticleCard key={a.slug} a={a} i={i} />)}
      </div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <>
      <Seo path="/" description="Solitaire India is a Mumbai-based renewable energy and solar advisory firm connecting C&I consumers, solar developers and EPC partners through commercially informed solar solutions." />
      <HomeHero />
      <Marquee />
      <Intro />
      <WhatWeDo />
      <Bento />
      <Solutions />
      <Process />
      <Philosophy />
      <PartnerTeaser />
      <Insights />
      <CTABand />
    </>
  );
}
